import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2, ExternalLink, Link2, Loader2 } from "lucide-react";
import api from "@/lib/api";

const ICON_OPTIONS = [
  { value: "fa-brands fa-linkedin", label: "LinkedIn" },
  { value: "fa-brands fa-github", label: "GitHub" },
  { value: "fa-brands fa-twitter", label: "Twitter" },
  { value: "fa-brands fa-instagram", label: "Instagram" },
  { value: "fa-brands fa-facebook", label: "Facebook" },
  { value: "fa-brands fa-youtube", label: "YouTube" },
  { value: "fa-brands fa-dribbble", label: "Dribbble" },
  { value: "fa-brands fa-behance", label: "Behance" },
  { value: "fa-solid fa-envelope", label: "Email" },
  { value: "fa-solid fa-link", label: "Link" },
  { value: "fa-solid fa-code", label: "Code" },
  { value: "fa-solid fa-laptop-code", label: "Laptop Code" },
  { value: "fa-solid fa-database", label: "Database" },
  { value: "fa-solid fa-server", label: "Server" },
  { value: "fa-brands fa-react", label: "React" },
  { value: "fa-brands fa-node-js", label: "Node.js" },
  { value: "fa-brands fa-python", label: "Python" },
  { value: "fa-brands fa-js", label: "JavaScript" },
  { value: "fa-brands fa-html5", label: "HTML5" },
  { value: "fa-brands fa-css3-alt", label: "CSS3" },
  { value: "fa-solid fa-pen-nib", label: "Design" },
  { value: "fa-solid fa-camera", label: "Photography" },
  { value: "fa-solid fa-gamepad", label: "Gaming" },
  { value: "fa-solid fa-music", label: "Music" },
  { value: "fa-solid fa-plane", label: "Travel" },
  { value: "fa-solid fa-book", label: "Reading" },
  { value: "fa-solid fa-dumbbell", label: "Fitness" },
  { value: "fa-solid fa-heart", label: "Heart" },
  { value: "fa-solid fa-star", label: "Star" },
  { value: "fa-solid fa-briefcase", label: "Briefcase" },
  { value: "fa-solid fa-chart-line", label: "Chart Line" },
  { value: "fa-solid fa-chart-pie", label: "Chart Pie" },
  { value: "fa-solid fa-users", label: "Users" },
  { value: "fa-solid fa-user-tie", label: "User Tie" },
  { value: "fa-solid fa-headset", label: "Support" },
  { value: "fa-solid fa-sack-dollar", label: "Money" },
  { value: "fa-solid fa-handshake", label: "Handshake" },
  { value: "fa-solid fa-bullhorn", label: "Marketing" },
  { value: "fa-solid fa-person-running", label: "Running" },
  { value: "fa-solid fa-person-swimming", label: "Swimming" },
  { value: "fa-solid fa-person-biking", label: "Biking" },
  { value: "fa-solid fa-mountain", label: "Hiking/Outdoors" },
  { value: "fa-solid fa-campground", label: "Camping" },
  { value: "fa-solid fa-futbol", label: "Soccer" },
  { value: "fa-solid fa-basketball", label: "Basketball" },
  { value: "fa-solid fa-medal", label: "Medal" },
  { value: "fa-solid fa-microphone", label: "Public Speaking" },
  { value: "fa-solid fa-chalkboard-user", label: "Teaching" },
  { value: "fa-solid fa-people-group", label: "Audience" },
  { value: "fa-solid fa-comments", label: "Discussion" },
  { value: "fa-solid fa-podcast", label: "Podcast" },
  { value: "fa-solid fa-pen-fancy", label: "Writing" }
];

const blank = { platformName: "", profileUrl: "", iconUrl: "", displayOrder: 1 };

export default function SocialLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/SocialLink');
      if (res.success) {
        // Sort by display order
        const sorted = (res.data || []).sort((a, b) => a.displayOrder - b.displayOrder);
        setItems(sorted);
      }
    } catch (err) {
      toast.error("Failed to load social links.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!form.platformName || !form.profileUrl) {
      toast.error("Platform name and Profile URL are required.");
      return;
    }

    try {
      const payload = {
        platformName: form.platformName,
        profileUrl: form.profileUrl,
        iconUrl: form.iconUrl,
        displayOrder: form.displayOrder ? parseInt(form.displayOrder, 10) : 1
      };

      if (editing) {
        const res = await api.put(`/SocialLink/${editing.id}`, payload);
        if (res.success) {
          toast.success("Social link updated.");
          fetchData();
        }
      } else {
        const res = await api.post('/SocialLink', payload);
        if (res.success) {
          toast.success("Social link added.");
          fetchData();
        }
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to save social link.");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await api.delete(`/SocialLink/${toDelete.id}`);
      if (res.success) {
        toast.success("Social link removed.");
        fetchData();
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete social link.");
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  const renderIcon = (iconStr) => {
    if (!iconStr) return <Link2 className="h-5 w-5" strokeWidth={1.5} />;
    
    // Support for FontAwesome classes (as requested)
    if (iconStr.includes("fa-") || iconStr.includes("fa ")) {
      return <i className={`${iconStr} text-lg`}></i>;
    }
    
    // Fallback to Lucide if matched
    const Icon = Icons[iconStr] || Link2;
    return <Icon className="h-5 w-5" strokeWidth={1.5} />;
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Social Links"
        description="Where you live across the social web."
        testId="social"
        action={() => { setEditing(null); setForm(blank); setModalOpen(true); }}
        actionLabel="Add Link"
        actionIcon={Plus}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <Card
            key={s.id}
            data-testid={`social-card-${s.id}`}
            className="group p-5 hover:border-foreground/20 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg bg-foreground text-background flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
                {renderIcon(s.iconUrl)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-base font-semibold tracking-tight">{s.platformName}</h3>
                  <a
                    href={s.profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-visit-${s.id}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </a>
                </div>
                <p className="text-xs font-mono text-muted-foreground mt-1 truncate">{s.profileUrl}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="secondary" className="text-[10px]">Order: {s.displayOrder}</Badge>
                  <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-7 w-7" 
                      onClick={() => { setEditing(s); setForm(s); setModalOpen(true); }} 
                      data-testid={`social-edit-${s.id}`}
                    >
                      <Pencil className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive"
                      onClick={() => { setToDelete(s); setConfirmOpen(true); }}
                      data-testid={`social-delete-${s.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="social-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit social link" : "Add social link"}</DialogTitle>
            <DialogDescription>Where this link lives on the social web.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Input data-testid="social-platform" value={form.platformName} onChange={(e) => setForm({ ...form, platformName: e.target.value })} className="mt-1.5" placeholder="LinkedIn" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Icon</Label>
                <Select value={form.iconUrl} onValueChange={(v) => setForm({ ...form, iconUrl: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="social-icon">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon.value} value={icon.value}>
                        <div className="flex items-center gap-2">
                          <i className={`${icon.value} w-4 text-center text-sm`}></i>
                          <span>{icon.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile URL</Label>
              <Input data-testid="social-url" value={form.profileUrl} onChange={(e) => setForm({ ...form, profileUrl: e.target.value })} className="mt-1.5 font-mono text-xs" placeholder="https://…" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display Order</Label>
              <Input data-testid="social-order" type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} className="mt-1.5" placeholder="1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="social-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete social link?"
        description="This will remove the social link from your portfolio."
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
